import {FC, ReactElement, useState} from 'react'
import {
    TreatmentRequestSmall,
    TreatmentRequestRejectedReasonValidationSchema
} from '@/features/treatment-requests/types.ts'
import {useTranslation} from '@/translations/i18n.tsx'
import {MenuButton, MenuProvider, useDialogStore} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {ListCheckboxIcon, QueueListIcon, SwitchVertical01Icon, XCloseIcon} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {useChangeStatus} from '@/features/treatment-requests/services/useChangeStatus.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {toast} from '@utilities/toast/toast.tsx'
import {
    StyledErrorToastContent,
    StyledSuccessToastContent
} from '@/features/treatment-requests/components/change-status-menu/style.ts'
import {isAxiosError} from 'axios'
import {FormProvider, useForm} from 'react-hook-form'
import {NotificationUsersConfirmationValidationSchema} from '@/features/users/types.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {ChangeStatusRejectedModal} from '@/features/treatment-requests/components/change-status-rejected-modal/ChangeStatusRejectedModal.tsx'
import {treatmentRequestStatusToLabel} from '@/features/treatment-requests/utils.ts'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {Text} from '@components/ui/text/Text'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useConversationForm} from '@/features/conversations/hooks/useConversationForm'

export const ChangeStatusMenu: FC<{
    trigger?: ReactElement
    selectedTreatmentRequests: TreatmentRequestSmall[]
    selectedTreatmentRequestsIds: string[]
    selectedTreatmentRequestsCount: number
    unselectedTreatmentRequestsIds: string[]
    patientIds: string[]
}> = ({
    trigger,
    selectedTreatmentRequestsIds,
    unselectedTreatmentRequestsIds,
    selectedTreatmentRequests,
    selectedTreatmentRequestsCount,
    patientIds
}) => {
    const {t} = useTranslation()
    const [isToInProgressConfirmationModalOpen, setIsToInProgressConfirmationModalOpen] = useState(false)
    const [isRejectedConfirmationModalOpen, setIsRejectedConfirmationModalOpen] = useState(false)
    const rejectedConfirmationDialogStore = useDialogStore({
        open: isRejectedConfirmationModalOpen,
        setOpen: setIsRejectedConfirmationModalOpen
    })
    const toInProgressConfirmationDialogStore = useDialogStore({
        open: isToInProgressConfirmationModalOpen,
        setOpen: setIsToInProgressConfirmationModalOpen
    })
    const statusToCheck = selectedTreatmentRequests[0]?.status
    const hasSameStatus = selectedTreatmentRequests.every(treatmentRequest => statusToCheck == treatmentRequest.status)
    const toInProgressForm = useForm<NotificationUsersConfirmationValidationSchema>()

    const {conversationForm, createConversation} = useConversationForm()

    const changeStatusMutation = useChangeStatus({
        onSuccess: async (_, variables) => {
            setIsToInProgressConfirmationModalOpen(false)
            rejectedConfirmationDialogStore.setOpen(false)

            if (patientIds.length == 1) {
                await createConversation(patientIds[0])
            }

            for (const patientId of patientIds) {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patientId]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            }

            toast.success(
                <StyledSuccessToastContent>
                    <h3>
                        {t('treatment_requests:change_status_success_title', {count: selectedTreatmentRequestsCount})}
                    </h3>
                    <p>
                        {t('treatment_requests:change_status_success_paragraph', {
                            prevStatus: t(treatmentRequestStatusToLabel[statusToCheck]),
                            nextStatus: t(treatmentRequestStatusToLabel[variables.payload.status])
                        })}
                    </p>
                </StyledSuccessToastContent>
            )
        },
        onError: error => {
            const errorCode =
                isAxiosError(error) &&
                typeof error.response?.data == 'object' &&
                error.response.data &&
                'code' in error.response.data &&
                error.response.data.code
                    ? error.response.data.code
                    : null

            toInProgressConfirmationDialogStore.hide()
            toast.error(
                <StyledErrorToastContent>
                    <h3>{t('treatment_requests:change_status_error_title')}</h3>
                    {errorCode == 'requests-with-different-statuses' && (
                        <p>{t('treatment_requests:change_status_error_different_statuses')}</p>
                    )}
                    {errorCode == 'requests-uncompleted' && (
                        <p>{t('treatment_requests:change_status_error_uncompleted_diagnosis_score')}</p>
                    )}
                    {errorCode == 'requests-with-pri' && <p>{t('treatment_requests:change_status_error_has_pri')}</p>}
                    {errorCode == 'diagnosis-code-required' && (
                        <p>{t('treatment_requests:change_status_error_diagnosis_code_required')}</p>
                    )}
                </StyledErrorToastContent>
            )
        }
    })

    if (!statusToCheck || !hasSameStatus || statusToCheck == 'accepted' || statusToCheck == 'rejected') {
        return false
    }

    const handleToInProgressFormSubmit = toInProgressForm.handleSubmit(values =>
        changeStatusMutation.mutate({
            payload: {
                notificationDetails: values.notificationDetails,
                notificationRecipientIds: values.notificationsRecipients?.map(user => user.id) ?? [],
                status: 'in_progress',
                selectedTreatmentRequestIds: selectedTreatmentRequestsIds,
                exceptedTreatmentRequestIds: unselectedTreatmentRequestsIds
            }
        })
    )

    const handleToRejectedFormSubmit = (rejectFormData: TreatmentRequestRejectedReasonValidationSchema) =>
        changeStatusMutation.mutate({
            payload: {
                status: 'rejected',
                selectedTreatmentRequestIds: selectedTreatmentRequestsIds,
                exceptedTreatmentRequestIds: unselectedTreatmentRequestsIds,
                notificationRecipientIds: rejectFormData.notificationsRecipients?.map(user => user.id) ?? [],
                ...rejectFormData
            }
        })

    return (
        <FormProvider {...toInProgressForm}>
            <MenuProvider>
                <MenuButton render={trigger ?? <Button variant="ghost" size="sm" />}>
                    <SwitchVertical01Icon />
                    {t('treatment_requests:change_status')}
                </MenuButton>
                <Menu width="220px">
                    <MenuGroup>
                        {(statusToCheck == 'in_review' || statusToCheck == 'in_progress') && (
                            <MenuItem
                                disabled={changeStatusMutation.isPending}
                                onClick={event => {
                                    event.preventDefault()
                                    changeStatusMutation.mutate({
                                        payload: {
                                            status: 'waiting_list',
                                            selectedTreatmentRequestIds: selectedTreatmentRequestsIds,
                                            exceptedTreatmentRequestIds: unselectedTreatmentRequestsIds
                                        }
                                    })
                                }}
                            >
                                <QueueListIcon />
                                {t('treatment_requests:status_waiting_list')}
                                {changeStatusMutation.isPending &&
                                    changeStatusMutation.variables?.payload.status == 'waiting_list' && <Spinner />}
                            </MenuItem>
                        )}
                        {statusToCheck == 'waiting_list' && (
                            <MenuItem
                                disabled={changeStatusMutation.isPending}
                                onClick={event => {
                                    event.preventDefault()
                                    setIsToInProgressConfirmationModalOpen(true)
                                }}
                            >
                                <ListCheckboxIcon />
                                {t('treatment_requests:status_in_progress')}
                                {changeStatusMutation.isPending &&
                                    changeStatusMutation.variables?.payload.status == 'in_progress' && <Spinner />}
                            </MenuItem>
                        )}
                        <MenuItem
                            disabled={changeStatusMutation.isPending}
                            onClick={event => {
                                event.preventDefault()
                                rejectedConfirmationDialogStore.setOpen(true)
                            }}
                        >
                            <XCloseIcon />
                            {t('treatment_requests:status_rejected', {count: 0})}
                            {changeStatusMutation.isPending &&
                                changeStatusMutation.variables?.payload.status == 'rejected' && <Spinner />}
                        </MenuItem>
                    </MenuGroup>
                </Menu>
                {isToInProgressConfirmationModalOpen && (
                    <ConfirmationDialog
                        dialogStore={toInProgressConfirmationDialogStore}
                        onConfirm={handleToInProgressFormSubmit}
                        isConfirmPending={changeStatusMutation.isPending}
                        conversationForm={patientIds.length == 1 ? conversationForm : undefined}
                        headerSlot={
                            <Flexbox direction="column" gap={2}>
                                <div>
                                    <Text palette="neutral" shade="600" size="textSm">
                                        {t('treatment_requests:change_status_confirm_title')}
                                    </Text>
                                    <DialogHeading>{t('treatment_requests:status_in_progress')}</DialogHeading>
                                </div>
                                <Text palette="neutral" shade="600" size="textSm">
                                    {t('treatment_requests:change_status_confirm_counter', {
                                        count: selectedTreatmentRequestsCount
                                    })}
                                </Text>
                            </Flexbox>
                        }
                    />
                )}
                {isRejectedConfirmationModalOpen && (
                    <ChangeStatusRejectedModal
                        dialogStore={rejectedConfirmationDialogStore}
                        onConfirm={handleToRejectedFormSubmit}
                        isConfirmPending={changeStatusMutation.isPending}
                        selectedTreatmentRequestsCount={selectedTreatmentRequestsCount}
                    />
                )}
            </MenuProvider>
        </FormProvider>
    )
}
